// Promotion image picker with sequential uploads, previews, count constraints,
// and per-file failures. Completed uploads remain selected when another fails.
// Removing a selection never deletes shared Storage assets or source images.
import React, { useRef, useState } from "react";
import { uploadPromotionImage, validatePromotionImage } from "../services/promotion_upload_service";

export default function PromotionImageUpload({ eventId, images = [], maxImages, onChange, disabled, onError, onBusyChange, onPreview }) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState("");
  const imagesRef = useRef(images);
  imagesRef.current = images;

  const addImages = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!files.length || uploading || disabled) return;
    if (imagesRef.current.length + files.length > maxImages) {
      onError(`This Post Type allows up to ${maxImages} image${maxImages === 1 ? "" : "s"}. Remove images or choose fewer files.`);
      return;
    }
    try { files.forEach(validatePromotionImage); }
    catch (error) { onError(error.message); return; }
    setUploading(true);
    onBusyChange?.(true);
    // Notify after every success so a later failure cannot erase completed work.
    let selected = [...imagesRef.current];
    try {
      for (let index = 0; index < files.length; index += 1) {
        setProgress(`Uploading image ${index + 1} of ${files.length}…`);
        try {
          const image = await uploadPromotionImage(files[index], eventId);
          selected = [...selected, image];
          imagesRef.current = selected;
          onChange(selected);
        } catch (error) {
          onError(`Could not upload ${files[index].name}: ${error.message || "Upload failed."} Completed images remain selected. Select this file again to retry.`);
          break;
        }
      }
    } finally {
      setUploading(false);
      setProgress("");
      onBusyChange?.(false);
    }
  };

  return <div className="mb-3">
    {maxImages > 0 && <>
    <label className="form-label" htmlFor="promotion-image-upload">Promotion Images</label>
    <input id="promotion-image-upload" className="form-control" type="file" multiple accept="image/jpeg,image/png,image/webp"
      disabled={disabled || uploading || images.length >= maxImages} onChange={addImages} />
    <div className="form-text">JPEG, PNG, or WebP; up to 5 MB each. This format allows {maxImages} image{maxImages === 1 ? "" : "s"}. Uploaded assets are publicly accessible; use public promotional content only.</div>
    </>}
    <div className="form-text">Review image dimensions using Post Sizes. Remove any image that does not fit; removal affects only this promotion, never the original record.</div>
    <div className="form-text">Original uploads stay unchanged. Generate Sized Image creates separate copies for the selected dimensions.</div>
    {maxImages === 0 && <div className="form-text">Choose an image-supported Post Type to include these images in the saved post.</div>}
    {uploading && <div role="status" className="small mt-2">{progress}</div>}
    {maxImages > 0 && images.length > maxImages && <div className="text-danger mt-2" role="alert">Remove images to meet this format’s limit of {maxImages}.</div>}
    <div className="d-flex flex-wrap gap-2 mt-2">
      {images.map((image, index) => <div className="border rounded p-2" key={`${image.url}-${index}`}>
        {onPreview ? <button type="button" className="btn p-0 border-0" aria-label={`Preview promotion image ${index + 1}`} onClick={() => onPreview(index)}>
          <img src={image.url} alt={`Promotion attachment ${index + 1}`} style={{ width: 110, height: 90, objectFit: "contain" }} />
        </button> : <img src={image.url} alt={`Promotion attachment ${index + 1}`} style={{ width: 110, height: 90, objectFit: "contain" }} />}
        <button type="button" className="btn btn-sm btn-outline-danger d-block mt-1" disabled={disabled || uploading}
          onClick={() => onChange(images.filter((_, imageIndex) => imageIndex !== index))}>Remove image {index + 1}</button>
      </div>)}
    </div>
    {!images.length && <div className="small text-muted mt-2">No promotion images selected.</div>}
  </div>;
}
